import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Layers, Sliders } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
});

const CHANNELS = ["edisplay", "funeral", "infinity"];
const CHANNEL_LABEL = { edisplay: "e-Displayroom", funeral: "Funeral", infinity: "Infinity" };
const CHANNEL_COLOR = {
  edisplay: "bg-green-100 text-green-700",
  funeral: "bg-rose-100 text-rose-700",
  infinity: "bg-blue-100 text-blue-700",
};

// Channel override is 'include' | 'exclude' | null(inherit). UI uses "inherit" for null.
const ovToUi = (v) => (v == null ? "inherit" : v);
const uiToOv = (v) => (v === "inherit" ? null : v);

/**
 * Source-of-truth catalog (Product Management v3).
 * Lists canonical products with their resolved channels + override state, and
 * lets an admin set per-product channel include/exclude overrides (which survive
 * collection re-tagging) plus the Infinity / eDisplay price overrides.
 * Wired to GET /admin/products/catalog + PUT /admin/products/:id/channels.
 */
export default function AdminCatalog() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [channel, setChannel] = useState("all");
  const [source, setSource] = useState("all");

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const limit = 25;

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (keyword) params.set("keyword", keyword);
      if (channel !== "all") params.set("channel", channel);
      if (source !== "all") params.set("source", source);
      const res = await axios.get(`${API}/admin/products/catalog?${params}`, getAuthHeaders());
      setProducts(res.data.data || []);
      setTotal(res.data.totalItems || 0);
    } catch (e) {
      toast.error("Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [page, keyword, channel, source]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      channel_overrides: {
        edisplay: ovToUi(p.channel_overrides?.edisplay),
        funeral: ovToUi(p.channel_overrides?.funeral),
        infinity: ovToUi(p.channel_overrides?.infinity),
      },
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        channel_overrides: {
          edisplay: uiToOv(form.channel_overrides.edisplay),
          funeral: uiToOv(form.channel_overrides.funeral),
          infinity: uiToOv(form.channel_overrides.infinity),
        },
      };
      const res = await axios.put(`${API}/admin/products/${editing.product_id}/channels`, payload, getAuthHeaders());
      toast.success(`Updated — channels: ${(res.data.effective_channels || []).join(", ") || "none"}`);
      setEditing(null);
      fetchCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-catalog">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Layers className="w-6 h-6" /> Master Catalog
        </h2>
        <p className="text-slate-500">All products managed by admin</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search</Label>
          <Input
            placeholder="Name or SKU…"
            value={keyword}
            onChange={(e) => { setPage(1); setKeyword(e.target.value); }}
          />
        </div>
        <div>
          <Label className="text-xs">Channel</Label>
          <Select value={channel} onValueChange={(v) => { setPage(1); setChannel(v); }}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNEL_LABEL[c]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Source</Label>
          <Select value={source} onValueChange={(v) => { setPage(1); setSource(v); }}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="shopify">Shopify</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Product</TableHead>
              <TableHead className="font-semibold">Source</TableHead>
              <TableHead className="font-semibold">Channels</TableHead>
              <TableHead className="font-semibold">Overrides</TableHead>
              <TableHead className="font-semibold text-right">Prices</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : products.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No products</TableCell></TableRow>
            ) : products.map((p) => {
              const ov = p.channel_overrides || {};
              const ovList = CHANNELS.filter((c) => ov[c]).map((c) => `${CHANNEL_LABEL[c]}:${ov[c]}`);
              return (
                <TableRow key={p.product_id} className="table-row">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.product_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs capitalize">{p.source || "shopify"}</span></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.effective_channels || []).length === 0
                        ? <span className="text-xs text-slate-400 italic">hidden</span>
                        : (p.effective_channels || []).map((c) => (
                          <span key={c} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CHANNEL_COLOR[c] || "bg-slate-100"}`}>{CHANNEL_LABEL[c] || c}</span>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-[11px] text-slate-500">{ovList.length ? ovList.join(", ") : "—"}</span>
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <div>base ${p.base_price}</div>
                    {p.overridden_price > 0 && <div className="text-green-700">eD ${p.overridden_price}</div>}
                    {p.infinity_overridden_price > 0 && <div className="text-blue-700">IT ${p.infinity_overridden_price}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                      <Sliders className="w-4 h-4 mr-1" /> Channels
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing <span className="font-medium">{total === 0 ? 0 : (page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, total)}</span> of <span className="font-medium">{total}</span> results
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            {/* Numbered Pagination Logic */}
            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0",
                        page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                      )}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Channel overrides — {editing?.name}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-500">
                <strong>Inherit</strong> = follow the product's collection tags. <strong>Include</strong>/<strong>Exclude</strong> force the channel on/off and survive collection re-tagging.
              </p>
              {CHANNELS.map((c) => (
                <div key={c} className="flex items-center justify-between gap-3">
                  <Label className="w-28">{CHANNEL_LABEL[c]}</Label>
                  <Select
                    value={form.channel_overrides[c]}
                    onValueChange={(v) => setForm({ ...form, channel_overrides: { ...form.channel_overrides, [c]: v } })}
                  >
                    <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit (from collections)</SelectItem>
                      <SelectItem value="include">Include</SelectItem>
                      <SelectItem value="exclude">Exclude</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">Override prices are managed on Platform → Products.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
